import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/page-title.tsx';
import { EventsOverviewHeaderNew } from '../../features/events-v2/components/events-overview-header-new.tsx';
import { EventsOverviewContentDay } from '../../features/events/components/events-overview-content-day.tsx';
import { EventsOverviewContentWeek } from '../../features/events/components/events-overview-content-week.tsx';
import { EventsOverviewHeader } from '../../features/events/components/events-overview-header.tsx';
import { useEventsListParams } from '../../features/events/hooks/use-events-list-params.ts';

export function useEventsAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta({
        path: '/events',
        title: t('page_title__events', { ns: 'common' }),
        requires: ['enableCustomerEventPlanning'],
        description:
            'The visit calendar by day or week: planned customer visits and other appointments for ' +
            'the signed-in user or a subordinate, with suggested customers to visit next. Visits can ' +
            'be one-off or recurring; a recurring visit repeats weekly (every N weeks on chosen ' +
            'weekdays, optionally until an end date, with single dates skippable). Daily, monthly or ' +
            'yearly cadences are not supported. Send the user here to plan or look up visits, ' +
            'including setting up a recurring visit. Visits that already took place are logged as ' +
            'notes and show in the activity feeds, not here.',
    });
}

function EventsPage() {
    const { t } = useTranslation();
    const { enableNewEventPage } = useAppConfig();
    const { params, days, day, setDay, viewMode, setViewMode, ownerId, setOwnerId } = useEventsListParams();

    return (
        <Page>
            <PageTitle title={t('page_title__events')} />
            {enableNewEventPage ? (
                <EventsOverviewHeaderNew
                    day={day}
                    onChangedDay={setDay}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    selectedOwnerId={ownerId}
                    onOwnerChange={setOwnerId}
                />
            ) : (
                <EventsOverviewHeader
                    day={day}
                    onChangedDay={setDay}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    selectedOwnerId={ownerId}
                    onOwnerChange={setOwnerId}
                />
            )}

            <hr />
            <div className="flex-1 overflow-y-auto">
                {viewMode === 'day' ? (
                    <EventsOverviewContentDay day={day} ownerId={ownerId} />
                ) : (
                    <EventsOverviewContentWeek
                        dateFrom={params.dateFrom}
                        dateTo={params.dateTo}
                        ownerId={ownerId}
                        days={days}
                    />
                )}
            </div>
        </Page>
    );
}

export default EventsPage;
